'use client';

import ReactSlick from '@fe-common-ui/Slick';
import { styled } from '@linaria/react';
import { useCallback, useMemo, useRef, useState } from 'react';
import Image from 'next/image';
import ColumnistsCard from './ColumnistsCard';
import DotsWrapper from '@fe-news/components/IndexMobileWrapper/NavigatorCarousel/DotsWrapper';
import useMediaQuery from '@fe-news/hooks/use-media-query';
import { SCREEN_SIZE } from '@fe-common-utils/constants/screen';

const StyledSlider = styled.div<{ isDesktop: boolean }>`
  position: relative;
  background-color: #fff;
  padding-bottom: 27px;

  img {
    border-radius: 10px;
  }

  &:hover {
    #left-arrow-touch,
    #right-arrow-touch {
      @media screen and (min-width: ${SCREEN_SIZE.DESKTOP}px) {
        display: block;
      }
    }
  }

  .slick-slide {
    margin: ${({ isDesktop }) => (isDesktop ? '0' : '0px 6.5px')};
  }

  .slick-track {
    display: ${({ isDesktop }) => (isDesktop ? 'block' : 'flex')};
  }
`;

const ArrowTouch = styled.button`
  position: absolute;
  top: 0;
  height: 100%;
  background-color: transparent;
  z-index: 2;
  display: none;
`;

interface ColumnistsCarouselInterface {
  columnistData: ColumnistsType[];
}

const getResponsiveSettings = (columnistCount: number, maximumSlidesToShow: number) => {
  const canSlide = columnistCount > maximumSlidesToShow;

  return {
    slidesToShow: Math.max(1, Math.min(columnistCount, maximumSlidesToShow)),
    slidesToScroll: 1,
    infinite: canSlide,
    autoplay: canSlide,
    centerMode: canSlide
  };
};

const ColumnistsCarousel: React.FC<ColumnistsCarouselInterface> = ({ columnistData }) => {
  const [scrollIndex, setScrollIndex] = useState(0);
  const sliderReference = useRef<null | {
    slickNext: () => void;
    slickPrev: () => void;
  }>(null);
  const isEmpty = columnistData?.length === 0;
  const columnistCount = columnistData.length;

  const isDesktop = useMediaQuery(`(min-width:${SCREEN_SIZE.DESKTOP}px)`, true);
  const isTablet = useMediaQuery(`(min-width:${SCREEN_SIZE.TABLET}px)`, true);
  const isLargeMobile = useMediaQuery('(min-width:500px)', true);
  const maximumSlidesToShow = isDesktop ? 5 : isTablet ? 4 : isLargeMobile ? 3 : 1;
  const canSlide = columnistCount > maximumSlidesToShow;

  const settings = useMemo(
    () => {
      const canSlideOnDesktop = columnistCount > 5;

      return {
        dots: false,
        className: isDesktop ? '' : 'center',
        centerMode: false,
        infinite: canSlideOnDesktop,
        centerPadding: '13px',
        slidesToShow: Math.max(1, Math.min(columnistCount, 5)),
        slidesToScroll: Math.max(1, Math.min(columnistCount, 5)),
        speed: 500,
        autoplay: canSlideOnDesktop,
        autoplaySpeed: 4000,
        beforeChange: (_: number, newIndex: number) => {
          const index = isDesktop && newIndex > 0 ? newIndex - 4 : newIndex;

          setScrollIndex(index);
        },
        responsive: [
          {
            breakpoint: 1024,
            settings: getResponsiveSettings(columnistCount, 4)
          },
          {
            breakpoint: 768,
            settings: getResponsiveSettings(columnistCount, 3)
          },
          {
            breakpoint: 500,
            settings: getResponsiveSettings(columnistCount, 1)
          }
        ]
      };
    },
    [columnistCount, isDesktop]
  );

  const onForceSlide = useCallback(
    (back = false) =>
      () => {
        if (sliderReference.current) {
          if (back) {
            sliderReference.current.slickNext();
          } else {
            sliderReference.current.slickPrev();
          }
        }
      },
    []
  );

  if (isEmpty) return null;

  const newsNumber = isDesktop ? Math.ceil(columnistCount / 5) : columnistCount;

  return (
    <StyledSlider isDesktop={isDesktop}>
      {canSlide && (
        <ArrowTouch style={{ left: -10 }} onClick={onForceSlide()} id='left-arrow-touch'>
          <Image src='/assets/icons/arrows/symbol-button-icon-back.png' alt='arrow back' width={36} height={36} />
        </ArrowTouch>
      )}
      <ReactSlick ref={sliderReference} {...settings}>
        {columnistData.map(columnist => {
          return <ColumnistsCard key={columnist.columnistsId} data={columnist} />;
        })}
      </ReactSlick>
      {canSlide && <DotsWrapper newsNumber={newsNumber} scrollIndex={scrollIndex} />}
      {canSlide && (
        <ArrowTouch style={{ right: -5 }} onClick={onForceSlide(true)} id='right-arrow-touch'>
          <Image src='/assets/icons/arrows/symbol-button-icon-next.png' alt='arrow back' width={36} height={36} />
        </ArrowTouch>
      )}
    </StyledSlider>
  );
};

export default ColumnistsCarousel;
